import {
  parseSearchBooleanValue,
  parseSearchNumberValue,
  parseSearchStringValue,
} from 'infrastructure/functions/parse-query-string';
import { useQueryFilterNew } from 'infrastructure/hooks/common/use-query-param-new';
import { Assignee, OverviewDashboardSortEnum } from 'infrastructure/enums';
import { endOfDay, startOfMonth } from 'date-fns';

import type { ParentClinicOption } from 'infrastructure/enums/parent-clinic';
import type {
  InteractionStatusEnum,
  Languages,
  ReadingStatus,
  ReadingType,
  Timezones,
} from 'infrastructure/enums';
import type { TParseFunctions } from 'infrastructure/hooks/common/use-query-param-new';
import type { PodFilterValue } from 'infrastructure/interfaces/ccm/pod';

export type TOverviewDashboardFilter = {
  page: number;
  reviewed: boolean;
  sorting: OverviewDashboardSortEnum;
  assignee?: Assignee;
  clinicGuid?: string;
  readingType?: ReadingType;
  status?: ReadingStatus;
  clinicState?: string;
  interactiveTimeRange?: string;
  interactionStatus?: InteractionStatusEnum;
  language?: Languages;
  timezone?: Timezones;
  parentClinicGuid?: string;
  patientGuid?: string;
  actionedByAgencyDoctorGuid?: string;
  startTime: number;
  endTime: number;
  parentClinicOption?: ParentClinicOption;
  pmpmStatus?: boolean;
  doctorGuid?: string;
  podFilter?: PodFilterValue;
};

const defaultFilter: TOverviewDashboardFilter = {
  page: 1,
  assignee: Assignee.All,
  reviewed: false,
  startTime: startOfMonth(new Date()).getTime(),
  endTime: endOfDay(new Date()).getTime(),
  sorting: OverviewDashboardSortEnum.Newest,
};

const VALID_POD_FILTER_VALUES: PodFilterValue[] = [
  'primary',
  'secondary',
  'tertiary',
  'outside',
];

const parseFunctions: TParseFunctions<TOverviewDashboardFilter> = {
  page: parseSearchNumberValue,
  clinicGuid: parseSearchStringValue,
  readingType: parseSearchStringValue,
  assignee: parseSearchStringValue,
  status: parseSearchStringValue,
  sorting: parseSearchStringValue,
  reviewed: parseSearchBooleanValue,
  clinicState: parseSearchStringValue,
  interactionStatus: parseSearchStringValue,
  startTime: parseSearchNumberValue,
  endTime: parseSearchNumberValue,
  interactiveTimeRange: parseSearchStringValue,
  language: parseSearchStringValue,
  timezone: parseSearchStringValue,
  parentClinicGuid: parseSearchStringValue,
  patientGuid: parseSearchStringValue,
  parentClinicOption: parseSearchStringValue,
  actionedByAgencyDoctorGuid: parseSearchStringValue,
  pmpmStatus: parseSearchBooleanValue,
  doctorGuid: parseSearchStringValue,
  podFilter: (value: string) =>
    VALID_POD_FILTER_VALUES.includes(value as PodFilterValue)
      ? (value as PodFilterValue)
      : undefined,
};

export const useOverviewDashboardFilter = () => {
  return useQueryFilterNew<TOverviewDashboardFilter>(
    defaultFilter,
    parseFunctions,
    ['page', 'endTime', 'startTime', 'assignee', 'podFilter'],
  );
};
